/**
 * 节点日志窗口组件
 *
 * 在工作流执行时显示节点的执行日志
 * 包含：
 * - LLM 请求和响应
 * - 工具调用记录
 * - 错误信息
 * - 执行统计（耗时、Token、工具调用次数）
 *
 * @module NodeLogPanel
 */

import React, { memo, useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { clsx } from 'clsx';
import { useExecutionStore } from '../../store/workflowStore';
import {
  ExecutionState,
  NodeExecutionStatus,
  NodeLogMessage,
  NodeExecutionLog,
} from '../../types/workflow';

// ==================== 类型定义 ====================

export interface NodeLogPanelProps {
  /** 自定义类名 */
  className?: string;
  /** 是否默认展开 */
  defaultExpanded?: boolean;
  /** 是否可折叠 */
  collapsible?: boolean;
  /** 是否显示标题 */
  showTitle?: boolean;
  /** 是否紧凑模式 */
  compact?: boolean;
  /** 最大高度 */
  maxHeight?: string;
  /** 是否自动滚动到底部 */
  autoScroll?: boolean;
  /** 当前选中的节点 ID（用于过滤显示） */
  selectedNodeId?: string | null;
  /** 日志消息点击回调 */
  onLogMessageClick?: (message: NodeLogMessage) => void;
}

// ==================== 工具函数 ====================

/**
 * 格式化时间戳为可读格式
 */
function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toLocaleTimeString('zh-CN', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * 格式化毫秒为可读时长
 */
function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
}

/**
 * 格式化 Token 数量
 */
function formatTokenCount(count: number): string {
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
  return count.toString();
}

/**
 * 安全的 JSON 字符串化
 */
function safeStringify(data: unknown): string {
  try {
    return JSON.stringify(data, null, 2);
  } catch {
    return String(data);
  }
}

// ==================== 子组件 ====================

/** 日志类型样式映射 */
const LOG_TYPE_STYLES: Record<NodeLogMessage['type'], { icon: React.ReactNode; color: string; bg: string }> = {
  info: {
    icon: (
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    color: 'text-blue-600',
    bg: 'bg-blue-50',
  },
  success: {
    icon: (
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    ),
    color: 'text-green-600',
    bg: 'bg-green-50',
  },
  warning: {
    icon: (
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
    color: 'text-yellow-600',
    bg: 'bg-yellow-50',
  },
  error: {
    icon: (
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    color: 'text-red-600',
    bg: 'bg-red-50',
  },
};

/** 单条日志消息组件 */
interface LogMessageItemProps {
  message: NodeLogMessage;
  isExpanded?: boolean;
  onToggle?: () => void;
  onClick?: () => void;
}

const LogMessageItem = memo<LogMessageItemProps>(({
  message,
  isExpanded = false,
  onToggle,
  onClick,
}) => {
  const style = LOG_TYPE_STYLES[message.type];
  const hasToolCall = message.tool_call !== undefined;
  const hasDetails = hasToolCall;

  return (
    <div
      className={clsx(
        'rounded-lg border overflow-hidden text-sm',
        style.bg,
        'border-gray-100',
      )}
    >
      {/* 消息头部 */}
      <div
        className={clsx(
          'flex items-start gap-2 px-3 py-2',
          onClick && 'cursor-pointer hover:bg-opacity-80',
        )}
        onClick={hasDetails ? onToggle : onClick}
      >
        {/* 类型图标 */}
        <span className={clsx('flex-shrink-0 mt-0.5', style.color)}>
          {style.icon}
        </span>

        {/* 消息内容 */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={clsx('font-medium', style.color)}>
              {message.message}
            </span>
            {hasToolCall && (
              <code className="px-1.5 py-0.5 bg-white/50 rounded text-xs text-gray-600">
                {message.tool_call?.tool_name}
              </code>
            )}
          </div>
          <div className="text-xs text-gray-400 mt-0.5">
            {formatTimestamp(message.timestamp)}
          </div>
        </div>

        {/* 展开/收起按钮 */}
        {hasDetails && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggle?.();
            }}
            className="p-1 hover:bg-white/50 rounded"
          >
            <svg
              className={clsx(
                'w-4 h-4 text-gray-400 transition-transform',
                isExpanded && 'rotate-180',
              )}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        )}
      </div>

      {/* 工具调用详情 */}
      {hasToolCall && isExpanded && (
        <div className="px-3 pb-2 border-t border-gray-100 bg-white/30">
          {/* 工具参数 */}
          {message.tool_call?.arguments && Object.keys(message.tool_call.arguments).length > 0 && (
            <div className="mt-2">
              <div className="text-xs font-medium text-gray-600 mb-1">参数：</div>
              <pre className="text-xs p-2 bg-white/50 rounded overflow-x-auto max-h-32">
                {safeStringify(message.tool_call.arguments)}
              </pre>
            </div>
          )}

          {/* 工具结果 */}
          {message.tool_call?.result !== undefined && (
            <div className="mt-2">
              <div className="text-xs font-medium text-gray-600 mb-1">结果：</div>
              <pre className="text-xs p-2 bg-white/50 rounded overflow-x-auto max-h-32">
                {safeStringify(message.tool_call.result)}
              </pre>
            </div>
          )}

          {/* 错误信息 */}
          {message.tool_call?.error && (
            <div className="mt-2">
              <div className="text-xs font-medium text-red-600 mb-1">错误：</div>
              <pre className="text-xs p-2 bg-red-50 text-red-700 rounded overflow-x-auto">
                {message.tool_call.error}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

LogMessageItem.displayName = 'LogMessageItem';

/** 执行统计组件 */
interface ExecutionStatsProps {
  log: NodeExecutionLog;
  compact?: boolean;
}

const ExecutionStats = memo<ExecutionStatsProps>(({ log, compact = false }) => {
  const toolCallCount = log.messages.filter((m) => m.tool_call).length;

  return (
    <div className={clsx(
      'flex items-center gap-4 py-2 px-3 bg-gray-50 border-t border-gray-200 text-xs',
      compact && 'gap-3',
    )}>
      {/* 执行耗时 */}
      {log.duration_ms !== undefined && (
        <div className="flex items-center gap-1">
          <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-gray-600">耗时:</span>
          <span className="font-medium text-gray-800">{formatDuration(log.duration_ms)}</span>
        </div>
      )}

      {/* Token 消耗 */}
      {log.token_usage && (
        <div className="flex items-center gap-1">
          <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
          </svg>
          <span className="text-gray-600">Token:</span>
          <span className="font-medium text-gray-800">
            {formatTokenCount(log.token_usage.input)} / {formatTokenCount(log.token_usage.output)} / 
            <span className="text-paper-700 ml-1">{formatTokenCount(log.token_usage.total)}</span>
          </span>
        </div>
      )}

      {/* 工具调用次数 */}
      {toolCallCount > 0 && (
        <div className="flex items-center gap-1">
          <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span className="text-gray-600">工具调用:</span>
          <span className="font-medium text-gray-800">{toolCallCount} 次</span>
        </div>
      )}
    </div>
  );
});

ExecutionStats.displayName = 'ExecutionStats';

/** 节点日志卡片组件 */
interface NodeLogCardProps {
  log: NodeExecutionLog;
  defaultExpanded?: boolean;
  compact?: boolean;
  onLogMessageClick?: (message: NodeLogMessage) => void;
}

const NodeLogCard = memo<NodeLogCardProps>(({
  log,
  defaultExpanded = true,
  compact = false,
  onLogMessageClick,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(new Set());

  // 状态样式
  const statusStyles: Record<NodeExecutionStatus, { color: string; bg: string; label: string }> = {
    [NodeExecutionStatus.PENDING]: { color: 'text-gray-600', bg: 'bg-gray-100', label: '等待中' },
    [NodeExecutionStatus.RUNNING]: { color: 'text-paper-700', bg: 'bg-paper-100', label: '执行中' },
    [NodeExecutionStatus.COMPLETED]: { color: 'text-green-600', bg: 'bg-green-100', label: '已完成' },
    [NodeExecutionStatus.FAILED]: { color: 'text-red-600', bg: 'bg-red-100', label: '失败' },
    [NodeExecutionStatus.SKIPPED]: { color: 'text-gray-500', bg: 'bg-gray-50', label: '已跳过' },
  };

  const statusStyle = statusStyles[log.status] || statusStyles[NodeExecutionStatus.PENDING];

  // 切换消息展开状态
  const toggleMessageExpand = useCallback((messageId: string) => {
    setExpandedMessages((prev) => {
      const next = new Set(prev);
      if (next.has(messageId)) {
        next.delete(messageId);
      } else {
        next.add(messageId);
      }
      return next;
    });
  }, []);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
      {/* 卡片头部 */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between gap-2"
      >
        <div className="flex items-center gap-2">
          <svg
            className={clsx(
              'w-4 h-4 text-gray-400 transition-transform',
              isExpanded && 'rotate-90',
            )}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="font-medium text-gray-800">{log.node_name}</span>
          <span className={clsx('text-xs px-2 py-0.5 rounded-full', statusStyle.bg, statusStyle.color)}>
            {statusStyle.label}
          </span>
        </div>
        <div className="text-xs text-gray-400">
          {log.duration_ms !== undefined ? formatDuration(log.duration_ms) : formatTimestamp(log.started_at)}
        </div>
      </button>

      {/* 卡片内容 */}
      {isExpanded && (
        <>
          {/* 日志消息列表 */}
          <div className="p-3 space-y-2 max-h-60 overflow-y-auto">
            {log.messages.length === 0 ? (
              <div className="text-sm text-gray-400 text-center py-4">暂无日志</div>
            ) : (
              log.messages.map((message) => (
                <LogMessageItem
                  key={message.id}
                  message={message}
                  isExpanded={expandedMessages.has(message.id)}
                  onToggle={() => toggleMessageExpand(message.id)}
                  onClick={() => onLogMessageClick?.(message)}
                />
              ))
            )}
          </div>

          {/* 执行统计 */}
          {(log.duration_ms || log.token_usage || log.messages.some((m) => m.tool_call)) && (
            <ExecutionStats log={log} compact={compact} />
          )}

          {/* 错误信息 */}
          {log.error && (
            <div className="px-3 py-2 bg-red-50 border-t border-red-100">
              <div className="text-xs font-medium text-red-600 mb-1">错误信息：</div>
              <pre className="text-xs text-red-700 whitespace-pre-wrap">{log.error}</pre>
            </div>
          )}
        </>
      )}
    </div>
  );
});

NodeLogCard.displayName = 'NodeLogCard';

// ==================== 空状态组件 ====================

const EmptyState = memo(() => (
  <div className="flex flex-col items-center justify-center py-8 text-gray-400">
    <svg className="w-12 h-12 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
    <p className="text-sm">暂无执行日志</p>
    <p className="text-xs mt-1">开始执行工作流后，日志将显示在这里</p>
  </div>
));

EmptyState.displayName = 'EmptyState';

// ==================== 主组件 ====================

export const NodeLogPanel = memo<NodeLogPanelProps>(({
  className,
  defaultExpanded = true,
  collapsible = true,
  showTitle = true,
  compact = false,
  maxHeight = '400px',
  autoScroll = true,
  selectedNodeId = null,
  onLogMessageClick,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const logsEndRef = useRef<HTMLDivElement>(null);

  // 从 store 获取数据
  const executionState = useExecutionStore((state) => state.executionState);
  const nodeLogs = useExecutionStore((state) => state.nodeLogs);
  const currentNodeId = useExecutionStore((state) => state.currentNodeId);
  const progress = useExecutionStore((state) => state.progress);
  const error = useExecutionStore((state) => state.error);

  // 将 Map 转换为数组并排序
  const sortedLogs = useMemo(() => {
    const logs = Array.from(nodeLogs.values());
    // 按开始时间排序
    logs.sort((a, b) => new Date(a.started_at).getTime() - new Date(b.started_at).getTime());
    return logs;
  }, [nodeLogs]);

  // 过滤日志（如果有选中的节点）
  const filteredLogs = useMemo(() => {
    if (!selectedNodeId) return sortedLogs;
    return sortedLogs.filter((log) => log.node_id === selectedNodeId);
  }, [sortedLogs, selectedNodeId]);

  // 是否正在执行
  const isExecuting = useMemo(() => {
    return [
      ExecutionState.RUNNING,
      ExecutionState.PAUSED,
      ExecutionState.STEP_WAITING,
    ].includes(executionState);
  }, [executionState]);

  // 是否有日志
  const hasLogs = filteredLogs.length > 0;

  // 自动滚动到底部
  useEffect(() => {
    if (autoScroll && isExpanded && hasLogs) {
      logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [autoScroll, isExpanded, hasLogs, filteredLogs]);

  // 计算总统计
  const totalStats = useMemo(() => {
    let totalDuration = 0;
    let totalInputTokens = 0;
    let totalOutputTokens = 0;
    let totalToolCalls = 0;

    filteredLogs.forEach((log) => {
      if (log.duration_ms) totalDuration += log.duration_ms;
      if (log.token_usage) {
        totalInputTokens += log.token_usage.input;
        totalOutputTokens += log.token_usage.output;
      }
      totalToolCalls += log.messages.filter((m) => m.tool_call).length;
    });

    return {
      duration: totalDuration,
      inputTokens: totalInputTokens,
      outputTokens: totalOutputTokens,
      totalTokens: totalInputTokens + totalOutputTokens,
      toolCalls: totalToolCalls,
    };
  }, [filteredLogs]);

  return (
    <div
      className={clsx(
        'bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden',
        compact ? 'text-xs' : 'text-sm',
        className,
      )}
    >
      {/* 标题栏 */}
      {showTitle && (
        <div
          className={clsx(
            'flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200',
            collapsible && 'cursor-pointer hover:bg-gray-100',
          )}
          onClick={() => collapsible && setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-2">
            {collapsible && (
              <svg
                className={clsx(
                  'w-4 h-4 text-gray-400 transition-transform',
                  isExpanded && 'rotate-90',
                )}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            )}
            <svg className="w-4 h-4 text-paper-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="font-medium text-gray-700">节点日志</span>
            {isExecuting && (
              <span className="text-xs text-paper-700 bg-paper-100 px-1.5 py-0.5 rounded">
                执行中
              </span>
            )}
            {progress.total > 0 && (
              <span className="text-xs text-gray-500">
                ({progress.completed}/{progress.total})
              </span>
            )}
          </div>

          {/* 总统计 */}
          {hasLogs && (
            <div className="flex items-center gap-3 text-xs text-gray-500">
              {totalStats.duration > 0 && (
                <span title="总耗时">{formatDuration(totalStats.duration)}</span>
              )}
              {totalStats.totalTokens > 0 && (
                <span title="总 Token">{formatTokenCount(totalStats.totalTokens)} tokens</span>
              )}
              {totalStats.toolCalls > 0 && (
                <span title="工具调用次数">{totalStats.toolCalls} 工具</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* 内容区域 */}
      {isExpanded && (
        <div
          className="p-3 space-y-2 overflow-y-auto"
          style={{ maxHeight }}
        >
          {/* 空状态 */}
          {!hasLogs && !isExecuting && <EmptyState />}

          {/* 日志卡片列表 */}
          {hasLogs && filteredLogs.map((log) => (
            <NodeLogCard
              key={log.node_id}
              log={log}
              defaultExpanded={log.node_id === currentNodeId}
              compact={compact}
              onLogMessageClick={onLogMessageClick}
            />
          ))}

          {/* 全局错误 */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="text-sm font-medium text-red-600 mb-1">执行错误</div>
              <pre className="text-xs text-red-700 whitespace-pre-wrap">{error}</pre>
            </div>
          )}

          {/* 滚动锚点 */}
          <div ref={logsEndRef} aria-hidden="true" />
        </div>
      )}
    </div>
  );
});

NodeLogPanel.displayName = 'NodeLogPanel';

export default NodeLogPanel;
